/**
 * Indicates the event/operation represented by a given action
 */
export interface Op<
  TOpName extends string = string,
  TOpStatus extends Op.Status | undefined = Op.Status | undefined,
> {
  name: TOpName
  status: TOpStatus
}
export namespace Op {
  /**
   * Evaluates if two {@link Op} are the same
   */
  export function isEqual<TOp extends Readonly<Op>>(
    a: Readonly<Op>,
    b: TOp,
  ): a is TOp {
    return a.name === b.name && a.status === b.status
  }

  /**
   * Indicates a stage of an {@link Op}, assuming the {@link Op} has stages. For
   * an {@link Op} that does not have stages, this will not be used
   *
   * Specifically:
   * - `request`: the {@link Op} is in the process of being executed
   * - `success`: the {@link Op} was executed successfully
   * - `error`: the {@link Op} failed to execute
   * - `cancellation`: the {@link Op} was cancelled
   *
   * __NOTE:__ this is a union of strings, not an enum, because it is rarely
   * referenced directly, and instead usually surfaced as a property in various
   * places. So, ease of reference is more valuable than ensuring direct
   * references
   */
  export type Status = 'request' | 'success' | 'error' | 'cancellation'
  export namespace Status {
    export type ForResponse = Exclude<Status, Satisfies<'request', Status>>

    export type Data<TOpStatus extends Op.Status, TRequest, TResponse> =
      // NOTE: AllowMissing.IgnoringAny has fewer type extension problems than
      // AllowMissing, but neither are perfect
      AllowMissing.IgnoringAny<Data.NoMissing<TOpStatus, TRequest, TResponse>>
    export namespace Data {
      export type NoMissing<
        TOpStatus extends Op.Status,
        TRequest,
        TResponse,
      > = {
        request: TRequest
        // NOTE: If.IsAny is used because AllowMissing.IgnoringAny can cause
        // problems in type extension when dealing with `any` in request or
        // response (AllowMissing has even more problems)
        success: If.IsAny<
          TRequest | TResponse,
          any,
          {
            request: TRequest
            response: TResponse
          }
        >
        cancellation: {
          request: TRequest
          reason: string
        }
        error: {
          request: TRequest
          error: Error
        }
      }[TOpStatus]

      export type Input<
        TOpStatus extends Op.Status,
        TRequestInput,
        TRequest,
        TResponseInput,
        TResponse,
      > = AllowUndefinedIfAllOptional<
        TOpStatus extends Satisfies<'request', Op.Status>
          ? TRequestInput
          : Input.WithoutRequest<TOpStatus, TResponseInput> &
              ResponseRequestReference<TRequestInput, TRequest>
      >
      export namespace Input {
        /**
         * __NOTE:__ this was introduced to resolve a very specific edge case.
         * In certain cases, we want to get the input for the response, but we
         * already have the associated request. In those cases, we tried to
         * {@link Omit}<{@link Op.Status.Data.Input}, 'request' | 'input'>.
         * However, when dealing with deferred evaluation of type parameters, we
         * ended up with problematic types that were still dependent on the
         * request types, even though they were not effectively being used
         * (TypeScript was deferring evaluation, so it could not determine that
         * they were not effectively being used). To resolve that, this type was
         * split off from {@link Op.Status.Data.Input} so that we didn't have to
         * {@link Omit} the request information.
         */
        export type WithoutRequest<
          TOpStatus extends Op.Status,
          TResponseInput,
        > = AllowUndefinedIfAllOptional<
          AllowMissing.IgnoringAny<
            {
              request: never
              success: {
                response: TResponseInput
              }
              cancellation: {
                reason?: string
              }
              error: {
                error?: Error
              }
            }[TOpStatus]
          >
        >
      }
    }
  }

  /**
   * Contains information about the original request. In case there is an input
   * transformer, this allows the use of either the input or the generated
   * request
   *
   * Typically, the generated request is more convenient when in effects, while
   * the input is more convenient in manual dispatches
   */
  // NOTE: we only check if the request can be undefined, not the request input,
  // because the input being undefined means some values might be generated, in
  // which case we want a request that contains those generated values
  type ResponseRequestReference<TRequestInput, TRequest> = If.IsAny<
    TRequest,
    // NOTE: Need to filter out `any` since that will be interpreted as
    // `undefined`, which causes type extension problems
    ResponseRequestReferenceBase<TRequestInput, TRequest>,
    undefined extends TRequest
      ? // If the request can be undefined, then we can accept not receiving
        // anything about the request
        Partial<ResponseRequestReferenceBase<TRequestInput, TRequest>>
      : ExactlyOneOf<ResponseRequestReferenceBase<TRequestInput, TRequest>>
  >

  // NOTE: the below being an interface causes weird issues downstream related to
  // this not being exported

  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  type ResponseRequestReferenceBase<TRequestInput, TRequest> = {
    request: TRequest
    input: TRequestInput
  }
}

type AllowUndefinedIfAllOptional<T> = If.IsAllOptional<T, T | undefined, T>
